import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

interface NavItem {
  to: string
  label: string
  badge?: number
}

interface NavGroupProps {
  icon: string
  label: string
  items: NavItem[]
}

export default function NavGroup({ icon, label, items }: NavGroupProps) {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)

  // Check if any child item is active
  const isActive = items.some(item => location.pathname === item.to || location.pathname.startsWith(item.to + '/'))

  // Auto-expand if a child is active
  useEffect(() => {
    if (isActive) {
      setIsOpen(true)
    }
  }, [isActive])

  return (
    <div className="space-y-1">
      {/* Group header button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition ${
          isActive
            ? 'bg-indigo-700 text-white'
            : 'text-indigo-200 hover:bg-indigo-700 hover:text-white'
        }`}
      >
        <span>{icon} {label}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Collapsible items */}
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-4 space-y-1 border-l-2 border-indigo-700 ml-3">
          {items.map(({ to, label: itemLabel, badge }) => {
            const itemIsActive = location.pathname === to || location.pathname.startsWith(to + '/')
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition ${
                  itemIsActive
                    ? 'bg-indigo-600 text-white'
                    : 'text-indigo-300 hover:bg-indigo-700 hover:text-white'
                }`}
              >
                <span>{itemLabel}</span>
                {badge !== undefined && badge > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {badge}
                  </span>
                )}
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
